import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createManual, fetchManualsByMasterAssembly } from "../../apiServices/manualsApi";
import { masterAssemblyApi } from "../../apiServices/masterassembly";
import { searchParts } from "../../apiServices/partsSearchApi";
import type { ManualDto } from "../../models/Manual";
import type { PartSearchResult } from "../../models/PartSearch";
import { useUserStore } from "../../store/userStore";
import "./CreateManual.css";

const CreateManual: React.FC = () => {
  const { masterAssemblyId } = useParams();
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);

  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "Oem1";

  const [manualType, setManualType] = useState("");
  const [title, setTitle] = useState("");
  const [reuseManualId, setReuseManualId] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [partResults, setPartResults] = useState<PartSearchResult[]>([]);
  const [manualResults, setManualResults] = useState<ManualDto[]>([]);
  const [selectedPart, setSelectedPart] = useState<PartSearchResult | null>(null);
  const [selectedManual, setSelectedManual] = useState<ManualDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const targetId = useMemo(() => Number(masterAssemblyId), [masterAssemblyId]);

  const handleSearchParts = async () => {
    setError(null);
    setMessage(null);
    setSelectedPart(null);
    setManualResults([]);
    setSelectedManual(null);
    try {
      const results = await searchParts(oem, searchKeyword);
      setPartResults(results);
    } catch (err: any) {
      setError(err?.message || "Failed to search parts");
    }
  };

  const handleSelectPart = async (part: PartSearchResult) => {
    setError(null);
    setMessage(null);
    setSelectedManual(null);
    setManualResults([]);
    setSelectedPart(part);
    try {
      let partMasterId = part.masterAssemblyId;
      if (!partMasterId) {
        const master = await masterAssemblyApi.getByOEMAndPartNumber(
          oem,
          part.componentPartNumber
        );
        partMasterId = master.masterAssemblyId;
      }
      if (partMasterId === targetId) {
        setError("Reference manual cannot be from the same part number.");
        return;
      }
      const manuals = await fetchManualsByMasterAssembly(oem, partMasterId);
      setManualResults(manuals);
    } catch (err: any) {
      setError(err?.message || "Failed to load manuals for selected part");
    }
  };

  const handleSelectManual = (manual: ManualDto) => {
    setSelectedManual(manual);
    setReuseManualId(String(manual.manualId));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetId || Number.isNaN(targetId)) {
      setError("Missing master assembly id.");
      return;
    }
    if (!manualType.trim() || !title.trim()) {
      setError("Manual Type and Title are required.");
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const payload = {
        masterAssemblyId: targetId,
        manualType: manualType.trim().toUpperCase(),
        title: title.trim(),
        status: "DRAFT",
        reviewerComments: null,
        version: 1,
        createdOn: new Date().toISOString(),
        updatedOn: null,
        createdBy: user?.username || "unknown",
        updatedBy: null,
      };
      const reuseIdNum = reuseManualId ? Number(reuseManualId) : undefined;
      await createManual(oem, payload, reuseIdNum);
      setMessage("Manual created.");
      navigate(`/authoring/view/${targetId}`);
    } catch (err: any) {
      setError(err?.message || "Failed to create manual");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-manual">
      <div className="create-manual__card">
        <div className="create-manual__header">
          <h2>Create Manual</h2>
        </div>
        <form className="create-manual__body" onSubmit={handleCreate}>
          <div className="create-manual__field">
            <label>Manual Type</label>
            <input
              value={manualType}
              onChange={(e) => setManualType(e.target.value)}
              placeholder="e.g. INSTALL"
            />
          </div>
          <div className="create-manual__field">
            <label>Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Manual title"
            />
          </div>
          <div className="create-manual__field">
            <label>Reuse Manual ID (optional)</label>
            <input
              value={reuseManualId}
              onChange={(e) => setReuseManualId(e.target.value)}
              placeholder="Manual ID to reuse content"
            />
          </div>

          <div className="create-manual__divider">Or select a reference manual</div>

          <div className="create-manual__search">
            <input
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearchParts();
                }
              }}
              placeholder="Search part number or description"
            />
            <button type="button" className="btn-primary" onClick={handleSearchParts}>
              Search
            </button>
          </div>

          {partResults.length > 0 && (
            <div className="create-manual__results">
              <div className="create-manual__results-title">Matching Parts</div>
              {partResults.map((part) => (
                <button
                  type="button"
                  key={`${part.componentPartNumber}-${part.masterAssemblyId ?? "x"}`}
                  className={`create-manual__result ${
                    selectedPart?.componentPartNumber === part.componentPartNumber ? "active" : ""
                  }`}
                  onClick={() => handleSelectPart(part)}
                >
                  <div className="create-manual__result-main">{part.componentPartNumber}</div>
                  <div className="create-manual__result-sub">
                    {part.componentDescription || "No description"}
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedPart && (
            <div className="create-manual__results">
              <div className="create-manual__results-title">
                Manuals for {selectedPart.componentPartNumber}
              </div>
              {manualResults.length > 0 ? (
                manualResults.map((manual) => (
                  <button
                    type="button"
                    key={manual.manualId}
                    className={`create-manual__result ${
                      selectedManual?.manualId === manual.manualId ? "active" : ""
                    }`}
                    onClick={() => handleSelectManual(manual)}
                  >
                    <div className="create-manual__result-main">
                      {manual.manualType} - {manual.title}
                    </div>
                    <div className="create-manual__result-sub">
                      Manual ID: {manual.manualId}
                    </div>
                  </button>
                ))
              ) : (
                <div className="create-manual__empty">No manuals found for this part.</div>
              )}
            </div>
          )}

          {error && <div className="create-manual__message error">{error}</div>}
          {message && <div className="create-manual__message success">{message}</div>}

          <div className="create-manual__footer">
            <button
              type="button"
              className="btn-outline"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Back
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? "Creating..." : "Create Manual"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateManual;
